/**
 * Button — Figma `Button/Primary` (instance 12:413) and `Button/Ghost` (6:142).
 *
 * Measured, not guessed: h-36, px-16, `radius/sm` (8px), `Body/14-Med`
 * (14/20, weight 500).
 *
 * ===========================================================================
 * WEB TIER A · WT1 — THE PRIMARY FILL IS THE D1a AUTHORED TRIO, NOT THE FIGMA
 * ACCENT. This file used to say "the primary fill is `accent/indigo` (1:26)";
 * that fill measured white-on-#5b6cfa = 4.22 ✗ in both modes, its hover was an
 * alpha fade (`bg-indigo/90` → 3.59 ✗ light / 4.85 ✓ dark — contrast moving
 * OPPOSITE directions per theme), and its active was worse (`/80` → 3.07 ✗).
 * The owner-accepted D1a ruling (docs/WEB-DESIGN-DIRECTION.md §6.2) replaces the FILL ONLY:
 * rest #4f5ed6 (5.37), hover #4a58cc (5.90), active #4553c4 (6.38) — every
 * state AA, contrast RISING through the states, in both modes, no opacity
 * modifier anywhere on the fill. `accent/indigo` itself is untouched and keeps
 * the focus ring, the tint, the selection rail and the checkbox. Pinned by
 * `button-fill.test.tsx` (classes) and `tokens.contrast.test.ts` (arithmetic).
 * ===========================================================================
 *
 * `ghost-on-dark` exists because the Country Picker's "Log out" (37:6804) sits on
 * the navy entry backdrop and overrides the component's border and text to
 * rgba(255,255,255,.3) / .9. Those two alphas are the design's own values on that
 * instance; they are white-on-navy overlay opacities, not new colours.
 *
 * `quiet` is the tile "Enter →" affordance (29:57): a `border/default` outline
 * with `accent/indigo` `Label/12` text, px-10/py-5.
 *
 * ===========================================================================
 * ACCESSIBILITY FIX (owner-ordered) — THE QUIET TEXT IS THE INK PAIR, NOT THE
 * FIGMA ACCENT. 29:57's `accent/indigo` at `Label/12` measured 3.90 ✗ dark /
 * 4.23 ✗ light on bg/surface, and its hover tint was `accent/indigo-soft` in
 * BOTH modes — a light chip flashing on a dark card, 3.72 ✗ under the pointer.
 * The same move as D1a above, for text: an authored per-mode pair
 * (`indigo-ink` 5.37/4.74 light · 6.17/4.93 dark, `indigo-wash` ≡ the design's
 * tint in light / indigo@20%-opaque in dark). `accent/indigo` is untouched.
 * Pinned by `button-fill.test.tsx` (classes) and `tokens.contrast.test.ts`
 * (arithmetic). Every `variant="quiet"` call site inherits this at once —
 * eleven files at the time of the fix; the hand-rolled `text-indigo` copies
 * outside Button (CountryPickerPage, LoginPage, NotFound, CountrySwitcher)
 * are NOT migrated here, by the same one-component rule the D1a pin records.
 * ===========================================================================
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'ghost' | 'ghost-on-dark' | 'quiet';

const BASE =
  'inline-flex items-center justify-center gap-2 font-medium transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-60 select-none';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'h-9 px-4 rounded-sm bg-indigo-strong text-fg-inverse text-body hover:bg-indigo-strong-hover active:bg-indigo-strong-active',
  ghost:
    'h-9 px-3 rounded-sm border border-line text-fg-secondary text-body hover:bg-subtle hover:text-fg',
  'ghost-on-dark':
    'h-9 px-4 rounded-sm border border-fg-inverse/30 text-fg-inverse/90 text-body hover:bg-fg-inverse/10 hover:text-fg-inverse',
  quiet:
    'px-2.5 py-[5px] rounded-sm border border-line text-indigo-ink text-label hover:bg-indigo-wash',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Stretch to the container. The sign-in button is `w-full` in Figma (12:413). */
  block?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', block = false, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, VARIANTS[variant], block && 'w-full', className)}
      {...rest}
    />
  );
});
